import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams, type To } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle2, X } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Spinner,
} from '@/components/ui';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { ComboboxOption } from '@/components/ui/combobox';
import { ApiErrorDisplay } from '@/components/features/error';
import { ChainList } from '@/components/features/services/chain-list';
import { RoutingStatusBadge } from '@/components/features/services/routing-status-badge';
import {
  MsisdnConfig,
  type MsisdnConfigValue,
  validateMsisdnUrl,
} from '@/components/features/services/msisdn-config';
import {
  RoutingEntryDialog,
  type RoutingEntryDialogMode,
  type IqGatewayServiceOption,
} from '@/components/features/services/routing-entry-dialog';
import { DiscardConfirmation } from '@/components/features/services/discard-confirmation';
import { GuardedLink } from '@/components/features/services/guarded-link';
import { LegacyMigrateBanner } from '@/components/features/services/legacy-migrate-banner';
import { useService, useUpdateRouting } from '@/hooks/use-services';
import { useIqServices } from '@/hooks/use-iq-services';
import { useIqServiceInventory } from '@/hooks/use-iq-service-inventory';
import { useUnsavedChangesPrompt } from '@/hooks/use-unsaved-changes-prompt';
import { useAuth } from '@/hooks/use-auth';
import { ApiError } from '@/lib/api/client';
import { ROLE_GROUPS } from '@/types/auth';
import type { FallbackChainEntry, Service } from '@/types/service';
import type { IqServiceInventoryStatus } from '@/types/iq-service-inventory';

const MAX_CHAIN_ENTRIES = 5;

interface DialogState {
  open: boolean;
  mode: RoutingEntryDialogMode;
  index?: number;
}

const CLOSED_DIALOG: DialogState = { open: false, mode: 'add' };

function deriveMsisdnDraft(service: Service | undefined): MsisdnConfigValue {
  const url = service?.msisdnCollectionUrl ?? '';
  return url ? { mode: 'custom', url } : { mode: 'facade', url: '' };
}

function reorder<T>(list: T[], from: number, to: number): T[] {
  if (from === to) return list;
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/**
 * Flach-Vergleich der Chain — gleich lang + alle Eintraege gleich
 * (Reihenfolge zaehlt, Strict-Equality auf jedem Feld).
 */
function isSameChain(a: FallbackChainEntry[], b: FallbackChainEntry[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((entry, i) =>
    entry.flowType === b[i].flowType
    && entry.gatewayServiceId === b[i].gatewayServiceId
    && (entry.iqServiceId ?? '') === (b[i].iqServiceId ?? ''),
  );
}

function isSameMsisdn(draft: MsisdnConfigValue, service: Service): boolean {
  const persisted = service.msisdnCollectionUrl ?? '';
  if (draft.mode === 'facade') return persisted === '';
  return draft.url.trim() === persisted;
}

/**
 * RoutingPage — Spec 0216 §5.3.
 *
 * Phase 2 (dieser Stand): Vollstaendige CRUD-Wired-UI. Add/Edit/Remove/
 * Reorder dispatchen lokal, "Routing speichern" sendet PUT mit
 * `fallbackChain` + `msisdnCollectionUrl` (immer explizit; siehe Spec
 * §4.2.1 zur Clearing-Semantik).
 *
 * Discard-Guard + Legacy-Migrate kommen in Phase 4 dazu; in dieser Phase
 * verlassen ungespeicherte Aenderungen die Seite stillschweigend (Plan §8).
 */
export function RoutingPage() {
  const { t } = useTranslation('routing');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: service, isLoading, error, refetch } = useService(id ?? '');
  const { data: iqServicesData, isLoading: iqLoading } = useIqServices();
  const { user } = useAuth();

  // RBAC — READ-Gate (inkl. PM): die Inventar-Abfragen + die Status-/Zaehler-Annotation
  // duerfen nur fuer Rollen im `IQ_SERVICE_INVENTORY_READ`-Set feuern. Diese Seite ist
  // `ROLE_GROUPS.ALL`; ohne dieses Gate erzeugten merchant_it/merchant_viewer einen
  // 403-Storm (List + N By-Key). NICHT `_ACCESS` (das schloesse PM aus — #9 muss intakt
  // bleiben, PM ist in READ). Die klickbaren Link-Gates (ACCESS/IQ_SERVICES_ACCESS,
  // platform-only) bleiben davon getrennt (unten).
  const canReadInventory =
    !!user && (ROLE_GROUPS.IQ_SERVICE_INVENTORY_READ as string[]).includes(user.role);
  // `undefined` merchantId → useIqServiceInventory bleibt disabled (kein Facade-Call).
  const inventoryMerchantId = canReadInventory ? service?.merchantId : undefined;

  // UC-5: the gatewayServiceId dropdown is sourced from this merchant's IQ-service
  // inventory (active only). Distinct from `useIqServices` above (credential sets).
  const iqInventory = useIqServiceInventory(inventoryMerchantId, { status: 'active', limit: 200 });
  // Spec 347 §7: SECOND query, status-UNFILTERED, feeds the routing<->inventory
  // linkage map (status badge + (a)-usage counter per chain entry). A FallbackChain
  // may reference an archived/pending key — `status:'active'` above would drop it, so
  // the linkage map must come from an unfiltered query (all statuses).
  const iqInventoryAll = useIqServiceInventory(inventoryMerchantId, { limit: 200 });
  const updateMutation = useUpdateRouting(id ?? '');

  // RBAC: link targets are platform-only routes. We render the value as plain text
  // (non-clickable) when the role lacks access; the status badge + counter stay visible
  // regardless (READ-gated, tenant-scoped). hasRole-conditional rendering, NOT a guard.
  const canViewInventory =
    !!user && (ROLE_GROUPS.IQ_SERVICE_INVENTORY_ACCESS as string[]).includes(user.role);
  const canViewCredential =
    !!user && (ROLE_GROUPS.IQ_SERVICES_ACCESS as string[]).includes(user.role);
  // Degradiert (List-Fehler / usageUnavailable) → Annotation zeigt „nicht ermittelbar".
  const inventoryUnavailable =
    canReadInventory &&
    (iqInventoryAll.isError ||
      (iqInventoryAll.data?.pages.flatMap((page) => page.items) ?? []).some(
        (item) => item.usageUnavailable,
      ));

  // Spec 352 §6: Seiten-Schranke erreicht (`usageTruncated`) → Zaehler unzuverlaessig,
  // Annotation zeigt „nicht ermittelbar" fuer den Zaehler (Status-Badge bleibt).
  const inventoryTruncated =
    canReadInventory &&
    (iqInventoryAll.data?.pages.flatMap((page) => page.items) ?? []).some(
      (item) => item.usageTruncated,
    );

  const inventoryStatusByKey = useMemo(() => {
    const map = new Map<string, IqServiceInventoryStatus>();
    for (const item of iqInventoryAll.data?.pages.flatMap((page) => page.items) ?? []) {
      map.set(item.iqServiceKey, item.status);
    }
    return map;
  }, [iqInventoryAll.data]);

  const usageByKey = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of iqInventoryAll.data?.pages.flatMap((page) => page.items) ?? []) {
      if (typeof item.fallbackUsageCount === 'number') {
        map.set(item.iqServiceKey, item.fallbackUsageCount);
      }
    }
    return map;
  }, [iqInventoryAll.data]);

  const [chainDraft, setChainDraft] = useState<FallbackChainEntry[]>([]);
  const [msisdnDraft, setMsisdnDraft] = useState<MsisdnConfigValue>({ mode: 'facade', url: '' });
  const [dialog, setDialog] = useState<DialogState>(CLOSED_DIALOG);
  const [confirmRemoveLast, setConfirmRemoveLast] = useState(false);
  const [msisdnError, setMsisdnError] = useState<string | undefined>();
  const [createBannerDismissed, setCreateBannerDismissed] = useState(false);
  const [discardTarget, setDiscardTarget] = useState<To | null>(null);
  const fromCreate = searchParams.get('from') === 'create' && !createBannerDismissed;

  // Spec 0216 §5.3 + §6.6 EC-7: isDirty steuert Discard-Guard + beforeunload.
  const isDirty = useMemo(() => {
    if (!service) return false;
    const persistedChain = service.fallbackChain ?? [];
    return !isSameChain(chainDraft, persistedChain) || !isSameMsisdn(msisdnDraft, service);
  }, [chainDraft, msisdnDraft, service]);

  useUnsavedChangesPrompt(isDirty);

  // Initialdaten in den Draft uebernehmen, sobald Service geladen ist.
  // Beim Wechsel auf einen anderen Service auch neu initialisieren.
  useEffect(() => {
    if (!service) return;
    setChainDraft(service.fallbackChain ?? []);
    setMsisdnDraft(deriveMsisdnDraft(service));
    setMsisdnError(undefined);
  }, [service?.serviceId, service?.fallbackChain, service?.msisdnCollectionUrl, service]);

  const iqServiceOptions: ComboboxOption[] = useMemo(
    () =>
      (iqServicesData?.data ?? []).map((svc) => ({
        // Issue #230: Diese Optionen befuellen das iqServiceId-Feld
        // (Credential-Set, `iq_…`) im Routing-Dialog — NICHT mehr das
        // gatewayServiceId-Feld. Das Gateway-Feld ist Freitext, weil
        // `IqServiceDetail` keine Gateway-IDs liefert.
        value: svc.iqServiceId,
        label: svc.displayName,
        description: `${svc.iqServiceId} · ${svc.merchantName} · ${svc.gatewayMode}`,
      })),
    [iqServicesData],
  );

  // UC-5: gatewayServiceId dropdown options from the merchant inventory (active),
  // carrying the flowType annotation so the dialog filters by the selected slot.
  const gatewayServiceOptions: IqGatewayServiceOption[] = useMemo(
    () =>
      (iqInventory.data?.pages.flatMap((page) => page.items) ?? []).map((item) => ({
        value: item.iqServiceKey,
        label: item.displayName ? `${item.displayName} (${item.iqServiceKey})` : item.iqServiceKey,
        flowType: item.flowType ?? null,
        status: item.status,
      })),
    [iqInventory.data],
  );

  if (!id) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">{t('error.missingId')}</p>
        <Button variant="link" onClick={() => navigate('/services')}>
          {t('common:button.back')}
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <ApiErrorDisplay error={error} onRetry={() => refetch()} />;
  }

  if (!service) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">{t('error.notFound')}</p>
        <Button variant="link" onClick={() => navigate('/services')}>
          {t('common:button.back')}
        </Button>
      </div>
    );
  }

  const isLegacy = chainDraft.length === 0 && !!service.gatewayServiceId;

  const handleRemove = (index: number) => {
    if (chainDraft.length <= 1) {
      setConfirmRemoveLast(true);
      return;
    }
    setChainDraft((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleEntrySubmit = (entry: FallbackChainEntry) => {
    if (dialog.mode === 'edit' && dialog.index !== undefined) {
      const targetIndex = dialog.index;
      setChainDraft((prev) => prev.map((e, i) => (i === targetIndex ? entry : e)));
    } else {
      setChainDraft((prev) => [...prev, entry]);
    }
    setDialog(CLOSED_DIALOG);
  };

  const handleSave = () => {
    // EC-2 (Spec §6.6): Chain-Mindest-Laenge.
    if (chainDraft.length === 0) {
      toast.error(t('save.errorEmptyChain'));
      return;
    }
    // EC-1 (Spec §6.6): UI begrenzt schon, defensive zweite Schranke.
    if (chainDraft.length > MAX_CHAIN_ENTRIES) {
      toast.error(t('save.errorTooMany'));
      return;
    }
    // MSISDN: Custom-Mode erfordert gueltige URL.
    const msisdnUrl: string | null =
      msisdnDraft.mode === 'custom' ? msisdnDraft.url.trim() : null;
    if (msisdnDraft.mode === 'custom') {
      if (!msisdnUrl) {
        setMsisdnError(t('save.errorMsisdnRequired'));
        toast.error(t('save.errorMsisdnRequired'));
        return;
      }
      const validationCode = validateMsisdnUrl(msisdnUrl);
      if (validationCode) {
        const validationError = t(`msisdn.validation.${validationCode}`);
        setMsisdnError(validationError);
        toast.error(validationError);
        return;
      }
    }
    setMsisdnError(undefined);

    updateMutation.mutate(
      { fallbackChain: chainDraft, msisdnCollectionUrl: msisdnUrl },
      {
        onSuccess: () => {
          toast.success(t('save.toastSaved'));
          navigate(`/services/${service.serviceId}`);
        },
        onError: (err) => {
          if (err instanceof ApiError && err.status === 409) {
            // EC-4 / Spec §9.1: Last-Write-Wins — kein Block, nur Hinweis.
            toast.success(t('save.toastSavedWithConcurrentChange'));
            navigate(`/services/${service.serviceId}`);
            return;
          }
          const message =
            err instanceof Error ? err.message : t('save.toastErrorGeneric');
          toast.error(message);
        },
      },
    );
  };

  const handleDiscard = () => {
    setChainDraft(service.fallbackChain ?? []);
    setMsisdnDraft(deriveMsisdnDraft(service));
    setMsisdnError(undefined);
  };

  const existingGatewayIds = chainDraft
    .filter((_, i) => i !== dialog.index)
    .map((e) => e.gatewayServiceId);

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <GuardedLink
            to={`/services/${service.serviceId}`}
            isDirty={isDirty}
            onBlocked={setDiscardTarget}
            variant="ghost"
            size="sm"
            className="mb-2 -ml-2"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('page.backToDetail')}
          </GuardedLink>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight">{t('page.title')}</h1>
            <RoutingStatusBadge
              fallbackChain={chainDraft.length > 0 ? chainDraft : undefined}
              gatewayServiceId={service.gatewayServiceId}
            />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('page.subtitle', {
              serviceId: service.serviceId,
              merchantId: service.merchantId,
            })}
          </p>
        </div>
      </div>

      {fromCreate && (
        <div
          className="mb-6 flex items-start justify-between gap-3 rounded-md border border-green-500/50 bg-green-50/70 p-3 text-sm text-green-900 dark:bg-green-950/20 dark:text-green-100"
          data-testid="from-create-banner"
        >
          <div className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
            <p>{t('page.createdBanner')}</p>
          </div>
          <button
            type="button"
            aria-label={t('page.createdBannerDismiss')}
            className="rounded p-1 hover:bg-green-100/60 dark:hover:bg-green-900/40"
            onClick={() => {
              setCreateBannerDismissed(true);
              const next = new URLSearchParams(searchParams);
              next.delete('from');
              setSearchParams(next, { replace: true });
            }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {isLegacy && <LegacyMigrateBanner service={service} />}

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">{t('chain.cardTitle')}</CardTitle>
          <CardDescription>{t('chain.cardDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <ChainList
            value={chainDraft}
            onAdd={() => setDialog({ open: true, mode: 'add' })}
            onEdit={(index) => setDialog({ open: true, mode: 'edit', index })}
            onRemove={handleRemove}
            onReorder={(from, to) => setChainDraft((prev) => reorder(prev, from, to))}
            inventoryStatusByKey={inventoryStatusByKey}
            usageByKey={usageByKey}
            merchantId={service.merchantId}
            inventorySettled={!iqInventoryAll.isLoading}
            canViewUsage={canReadInventory}
            inventoryUnavailable={inventoryUnavailable}
            inventoryTruncated={inventoryTruncated}
            canViewInventory={canViewInventory}
            canViewCredential={canViewCredential}
            onOpenInventoryKey={(key) => {
              // EC-7: Link-Navigation respektiert den Discard-Guard. Bei ungespeicherten
              // Aenderungen erst die Confirmation oeffnen (navigate uebernimmt der Dialog).
              const target = `/merchants/${service.merchantId}/iq-services?key=${encodeURIComponent(key)}`;
              if (isDirty) setDiscardTarget(target);
              else navigate(target);
            }}
            onOpenCredential={(iqId) => {
              const target = `/iq-services/${iqId}`;
              if (isDirty) setDiscardTarget(target);
              else navigate(target);
            }}
          />
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">{t('msisdn.cardTitle')}</CardTitle>
          <CardDescription>{t('msisdn.cardDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <MsisdnConfig
            value={msisdnDraft}
            onChange={(next) => {
              setMsisdnDraft(next);
              setMsisdnError(undefined);
            }}
            error={msisdnError}
          />
        </CardContent>
      </Card>

      <div className="flex flex-col gap-2 md:flex-row md:justify-end">
        <Button
          variant="outline"
          onClick={handleDiscard}
          disabled={updateMutation.isPending}
        >
          {t('save.buttonDiscard')}
        </Button>
        <Button onClick={handleSave} disabled={updateMutation.isPending}>
          {updateMutation.isPending ? t('save.buttonSaving') : t('save.buttonSave')}
        </Button>
      </div>

      <RoutingEntryDialog
        open={dialog.open}
        mode={dialog.mode}
        initial={
          dialog.mode === 'edit' && dialog.index !== undefined
            ? chainDraft[dialog.index]
            : undefined
        }
        existingGatewayIds={existingGatewayIds}
        iqServiceOptions={iqServiceOptions}
        iqServiceOptionsLoading={iqLoading}
        gatewayServiceOptions={gatewayServiceOptions}
        gatewayServiceOptionsLoading={iqInventory.isLoading}
        onSubmit={handleEntrySubmit}
        onCancel={() => setDialog(CLOSED_DIALOG)}
      />

      <DiscardConfirmation
        open={discardTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDiscardTarget(null);
        }}
        onConfirm={() => {
          const target = discardTarget;
          setDiscardTarget(null);
          if (target !== null) navigate(target);
        }}
      />

      <AlertDialog open={confirmRemoveLast} onOpenChange={setConfirmRemoveLast}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('removeLast.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('removeLast.description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setConfirmRemoveLast(false)}>
              {t('removeLast.ok')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
